import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { useDashboardStore } from '../DashboardStore'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import DashboardActiveFilterPill from './DashboardActiveFilterPill'
import { PerAvatarDashboardBuManagerTooltipRow } from '../../../components/custom_components/AvatarPlus/PerAvatarDashboardMonitoringTable'

type ActiveFilterItem = {
  key: string
  label: string
  value: string
  onClear: () => void
  afterPill?: ReactNode
}

const DashboardAppraisalMonitoringActiveFilters: FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [appraisalMonitoringFilters, setAppraisalMonitoringFilters] = useDashboardStore((state) => [
    state.appraisalMonitoringFilters,
    state.setAppraisalMonitoringFilters,
  ])

  const selectedBuWithManagers = appraisalMonitoringFilters.selectedBusinessUnit
  const buManagersForSelectedBu = selectedBuWithManagers?.buManagers ?? []

  const labelKey = 'dashboard.companyManager.filtersArea.distributions.activeFilterLabels'

  const buLabel = !selectedBuWithManagers ? undefined : selectedBuWithManagers.businessUnit.displayName
  const bumLabel = (() => {
    if (!appraisalMonitoringFilters.selectedBuManager) {
      return undefined
    }
    const m = appraisalMonitoringFilters.selectedBuManager
    return computeFullname(user.nameDisplayPreference as NameDisplayPreference, m.firstName, m.lastName)
  })()
  const officeLabel = !appraisalMonitoringFilters.selectedBranchOffice ? undefined : appraisalMonitoringFilters.selectedBranchOffice.code
  const levelsLabel = !appraisalMonitoringFilters.selectedEmployeeLevels
    ? undefined
    : appraisalMonitoringFilters.selectedEmployeeLevels.label

  const activeItems = [
    levelsLabel
      ? {
          key: 'levels',
          label: `${t(`${labelKey}.level`)}:`,
          value: levelsLabel,
          onClear: () =>
            setAppraisalMonitoringFilters({
              ...appraisalMonitoringFilters,
              selectedEmployeeLevels: undefined,
            }),
        }
      : null,
    officeLabel
      ? {
          key: 'office',
          label: `${t(`${labelKey}.office`)}:`,
          value: officeLabel,
          onClear: () =>
            setAppraisalMonitoringFilters({
              ...appraisalMonitoringFilters,
              selectedBranchOffice: undefined,
            }),
        }
      : null,
    buLabel
      ? {
          key: 'bu',
          label: `${t(`${labelKey}.businessUnit`)}:`,
          value: buLabel,
          onClear: () =>
            setAppraisalMonitoringFilters({
              ...appraisalMonitoringFilters,
              selectedBusinessUnit: undefined,
            }),
          afterPill:
            buManagersForSelectedBu.length > 0 ? (
              <div className="dashboard-appraisal-monitoring__active-filters-bu-managers">
                {buManagersForSelectedBu.map((manager) => (
                  <PerAvatarDashboardBuManagerTooltipRow
                    showBuManagerLabel
                    key={String(manager.id)}
                    manager={manager}
                    nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
                  />
                ))}
              </div>
            ) : undefined,
        }
      : null,
    bumLabel
      ? {
          key: 'bum',
          label: `${t(`${labelKey}.buManagers`)}:`,
          value: bumLabel,
          onClear: () =>
            setAppraisalMonitoringFilters({
              ...appraisalMonitoringFilters,
              selectedBuManager: undefined,
            }),
        }
      : null,
  ].filter(Boolean) as ActiveFilterItem[]

  if (activeItems.length === 0) {
    return null
  }

  return (
    <div className="dashboard-appraisal-monitoring__active-filters">
      {activeItems.map((item) => (
        <PerFlexRow key={item.key} align="middle" noWrap>
          <PerText color="secondary" fontSize="xs" mr="xxs">
            {item.label}
          </PerText>
          <DashboardActiveFilterPill selected closeAlwaysVisible onClick={item.onClear}>
            {item.value}
          </DashboardActiveFilterPill>
          {item.afterPill}
        </PerFlexRow>
      ))}
    </div>
  )
}

export default DashboardAppraisalMonitoringActiveFilters
